// @ts-nocheck
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Kanban, Printer, CheckCircle2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { toast } from 'sonner';
import { PrepressStatus } from '@prisma/client';
import CTPPlateDetailModal from './components/CTPPlateDetailModal';

type LayoutData = {
  id: string;
  name: string;
  status: PrepressStatus;
  layoutCode: string | null;
  version: string | null;
  plateCount: number | null;
  productionOrderId: string | null;
  createdAt: string;
};

// Map các trạng thái CTP
const CTP_STAGES = [
  { id: 'PENDING', label: 'Chờ xử lý', color: 'bg-slate-100 text-slate-800', icon: Kanban },
  { id: 'PLATE_OUTPUT', label: 'Đang ghi kẽm', color: 'bg-blue-100 text-blue-800', icon: Printer },
  { id: 'COMPLETED', label: 'Hoàn thành', color: 'bg-green-100 text-green-800', icon: CheckCircle2 }
] as const;

export const CTPTrackingPage = () => {
  const queryClient = useQueryClient();
  const [selectedLayout, setSelectedLayout] = useState<any | null>(null);

  // Fetch layouts
  const { data: rawLayouts = [], isLoading, refetch } = useQuery({
    queryKey: ['ctp-tracking-layouts'],
    queryFn: async () => {
      const result = await trpcClient.prepress.listPrintLayouts.query();
      return (Array.isArray(result) ? result : (result as any)?.items || []) as LayoutData[];
    }
  });

  const layouts = Array.isArray(rawLayouts) ? rawLayouts : (rawLayouts as any)?.items || [];

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string, status: PrepressStatus }) => {
      return trpcClient.prepress.updatePrintLayoutStatus.mutate({ id, status });
    },
    onSuccess: () => {
      toast.success('Đã cập nhật trạng thái kẽm');
      queryClient.invalidateQueries({ queryKey: ['ctp-tracking-layouts'] });
    },
    onError: (error) => {
      toast.error('Lỗi khi cập nhật trạng thái: ' + error.message);
    }
  });

  const getLayoutsByStatus = (statusId: string) => {
    return layouts.filter((layout: any) => {
      if (statusId === 'PENDING') {
        return ['PENDING', 'LAYOUT_PROCESSING', 'MOLD_ORDERED', 'DRAFT'].includes(layout.status);
      }
      return layout.status === statusId;
    });
  };

  const handleMoveTo = (layoutId: string, nextStatus: PrepressStatus) => {
    updateStatusMutation.mutate({ id: layoutId, status: nextStatus });
  };

  return (
    <div className="w-full px-4 sm:px-6 py-4 space-y-4">
      <div className="flex justify-between items-center bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-xs">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">Tiến Độ Ghi Kẽm CTP</span>
          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600">
            {layouts.length} bản bình
          </Badge>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Làm mới
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CTP_STAGES.map((stage) => {
            const Icon = stage.icon;
            const stageLayouts = getLayoutsByStatus(stage.id);
            
            return (
              <div key={stage.id} className="flex flex-col gap-4">
                <div className={`p-4 rounded-lg flex items-center justify-between ${stage.color}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" />
                    <h3 className="font-semibold">{stage.label}</h3>
                  </div>
                  <Badge variant="secondary" className="bg-white/50">{stageLayouts.length}</Badge>
                </div>
                
                <div className="flex-1 space-y-4 rounded-xl bg-slate-50/50 p-4 border border-slate-100 min-h-[500px]">
                  {stageLayouts.length === 0 ? (
                    <div className="text-center p-8 text-slate-400 text-sm">
                      Không có bản bình nào
                    </div>
                  ) : (
                    stageLayouts.map((layout) => (
                      <Card
                        key={layout.id}
                        className="shadow-sm border-slate-200 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all group"
                        onClick={() => setSelectedLayout(layout)}
                      >
                        <CardHeader className="p-4 pb-2">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-sm font-semibold truncate group-hover:text-indigo-600 transition-colors" title={layout.name}>
                              {layout.layoutCode || 'Chưa có mã'}
                            </CardTitle>
                            <Badge variant="outline" className="text-xs shrink-0 font-mono">
                              {layout.version || 'v1'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="p-4 pt-2">
                          <div className="space-y-2 text-sm text-slate-600 mb-4">
                            <p className="truncate">Tên: <span className="font-medium text-slate-900">{layout.name}</span></p>
                            <p>Số lượng kẽm: <span className="font-medium text-slate-900">{layout.plateCount || '?'}</span></p>
                            {layout.productionOrderId && (
                              <p className="text-xs text-slate-400">Đã tạo LSX</p>
                            )}
                          </div>
                          
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            {stage.id === 'PENDING' && (
                              <Button 
                                size="sm" 
                                className="w-full"
                                onClick={() => handleMoveTo(layout.id, 'PLATE_OUTPUT')}
                                disabled={updateStatusMutation.isPending}
                              >
                                Bắt đầu ghi
                              </Button>
                            )}
                            {stage.id === 'PLATE_OUTPUT' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => handleMoveTo(layout.id, 'PENDING')}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  Hủy
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  onClick={() => handleMoveTo(layout.id, 'COMPLETED')}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  Xong
                                </Button>
                              </>
                            )}
                            {stage.id === 'COMPLETED' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="w-full text-slate-500"
                                onClick={() => handleMoveTo(layout.id, 'PLATE_OUTPUT')}
                                disabled={updateStatusMutation.isPending}
                              >
                                Làm lại
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Chi Tiết Bản Kẽm CTP */}
      <CTPPlateDetailModal
        open={!!selectedLayout}
        onOpenChange={(open) => !open && setSelectedLayout(null)}
        layout={selectedLayout}
        onStatusChange={(layoutId, status) => handleMoveTo(layoutId, status)}
      />
    </div>
  );
};
